import { useNavigate } from '@tanstack/react-router'
import {
  ColumnDef,
  PaginationState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import { Typography } from 'antd'
import { TableComponent } from 'components/table/TableComponent'
import { useRolodexEntries } from 'features/rolodex'
import startCase from 'lodash/startCase'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoUnit, InputType, RolodexEntryType } from 'types'
import { associateBy } from 'utils/fde/maps'
import { parseString } from 'utils/parsingHelpers'

const { Text } = Typography

const NoUnits = styled.div`
  padding: 16px;
  text-align: center;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const ClickableAssetName = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.text1};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
    text-decoration: underline;
  }

  &:active {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

const StyledCheckbox = styled.input`
  cursor: pointer;
  margin: 0;

  &:disabled {
    cursor: not-allowed;
  }
`

interface WalkdownUnitListProps {
  units: readonly GeckoUnit[]
  onSelectedUnitsChange?: (selectedUnitIds: string[]) => void
}

export const WalkdownUnitList: React.FC<WalkdownUnitListProps> = ({
  units,
  onSelectedUnitsChange,
}) => {
  const navigate = useNavigate()
  const { assetModels } = useRolodexEntries({
    type: [RolodexEntryType.ASSET_MODEL],
    tags: {
      unit: units.map((unit) => unit.id),
    },
  })

  // Table state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const unitData = useMemo(() => {
    const assetModelByUnit = associateBy({
      arr: assetModels,
      key: 'tags.unit',
    })

    return units
      .filter((unit) => unit.data?.name)
      .map((unit) => {
        const assetModel = assetModelByUnit.get(unit.id)
        return {
          ...unit,
          assetType: assetModel?.data?.type,
        }
      })
  }, [units, assetModels])

  const handleAssetNameClick = useCallback(
    (unit: GeckoUnit) => {
      const siteID = unit.tags.site
      if (siteID) {
        navigate({
          to: '/assets/$siteID/$unitID',
          params: { siteID, unitID: unit.id },
          search: { view: 'details' },
        })
      }
    },
    [navigate]
  )

  // Handle row selection changes
  const handleRowSelectionChange = useCallback(
    (
      updaterOrValue:
        | RowSelectionState
        | ((old: RowSelectionState) => RowSelectionState)
    ) => {
      const newRowSelection =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(rowSelection)
          : updaterOrValue
      setRowSelection(newRowSelection)

      // Convert row selection state to unit IDs and notify parent
      const selectedUnitIds = Object.keys(newRowSelection).filter(
        (key) => newRowSelection[key]
      )
      onSelectedUnitsChange?.(selectedUnitIds)
    },
    [rowSelection, onSelectedUnitsChange]
  )

  const columns = useMemo<
    ColumnDef<GeckoUnit & { assetType?: string }>[]
  >(() => {
    const baseColumns: ColumnDef<GeckoUnit & { assetType?: string }>[] = [
      {
        id: 'assetName',
        header: 'Asset Name',
        accessorFn: (row) => row.data?.name,
        cell: ({ row }) => {
          const assetName = parseString(row.original.data?.name)
          return (
            <ClickableAssetName
              onClick={(e) => {
                e.stopPropagation()
                handleAssetNameClick(row.original)
              }}
              title='Click to view asset details'>
              {assetName}
            </ClickableAssetName>
          )
        },
        meta: {
          inputType: 'text' as InputType,
        },
      },
      {
        id: 'assetType',
        header: 'Asset Type',
        accessorKey: 'assetType',
        cell: ({ getValue }) => {
          const assetType = getValue() as string | undefined
          return (
            <Text type='secondary'>
              {assetType ? startCase(assetType) : 'N/A'}
            </Text>
          )
        },
        meta: {
          inputType: 'text' as InputType,
        },
      },
    ]

    // Add selection column if row selection is enabled
    if (onSelectedUnitsChange) {
      return [
        {
          id: 'select',
          header: ({ table }) => {
            return (
              <StyledCheckbox
                type='checkbox'
                checked={table.getIsAllRowsSelected()}
                ref={(el) => {
                  if (el) el.indeterminate = table.getIsSomeRowsSelected()
                }}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            )
          },
          cell: ({ row }) => {
            return (
              <StyledCheckbox
                type='checkbox'
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                onChange={row.getToggleSelectedHandler()}
              />
            )
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 40,
          meta: {
            inputType: 'checkbox' as InputType,
          },
        },
        ...baseColumns,
      ]
    }

    return baseColumns
  }, [handleAssetNameClick, onSelectedUnitsChange])

  if (unitData.length === 0) {
    return <NoUnits>No units available</NoUnits>
  }

  return (
    <TableComponent
      data={unitData}
      columns={columns}
      isLoadingData={false}
      footerName='units'
      pagination={pagination}
      setPagination={setPagination}
      sorting={sorting}
      setSorting={setSorting}
      enableRowSelection={onSelectedUnitsChange ? true : false}
      rowSelection={rowSelection}
      onRowSelectionChange={
        onSelectedUnitsChange ? handleRowSelectionChange : undefined
      }
      getRowId={(row) => row.id}
      showFilterBar={false}
      showColumnConfig={false}
      showTable={true}
    />
  )
}
